import React, { useEffect, useState } from "react";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import i18n from "utils/i18n";
import _ from "lodash";
import { FormStatus } from 'utils/constants/form-constants';
import GraphqlBuilder from "utils/request/graphql/builder";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import LoadingComponent from "components/atoms/Loading";
import Translate from "utils/translate";
import QueryManagementRoom from "graphql/queries/management/QueryManagementRoom";
import HelperString from 'utils/helper-string'
import MutationManagementRoom from 'graphql/mutations/management/MutationManagementRoom'
import MutateTranslation from 'utils/mutate/translation'
import { tableConstants } from 'utils/constants/constants'
import ObjectRoom from 'utils/objects/room'
import HelperAvailability from "utils/helper/HelperAvailability";
import ManagementBuildingsComponentsTimeslotsScene from "scenes/management/buildings/components/timeslots";
import ManagementBuildingsComponentsTeamsScene from "scenes/management/buildings/components/teams";
import ManagementBuildingsComponentsEmployeeScene from "scenes/management/buildings/components/employees";
import ObjectTimeslot from "utils/objects/timeslot";
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import RoomStatistics from 'scenes/management/buildings/components/room/statistics'
import TabbedContent from 'components/molecules/TabbedContent'
import { AppRoutes } from 'navigations/app-routes'
import RoomData from 'scenes/management/buildings/components/room/data'
import ManagementBuildingsComponentsTimeslotExceptionsScene from 'scenes/management/buildings/components/timeslotsExceptions'

const ManagementBuildingRoomScene = () => {
	
	const viewTabs = {
		STATISTICS: 'statistics',
		DATA: 'data',
		TIMESLOTS: 'timeslots',
		TIMESLOT_EXCEPTIONS: 'timeslot_exceptions',
		AVAILABLE_FOR_TEAMS: 'teams',
		AVAILABLE_FOR_EMPLOYEES: 'employees'
	};
	
	const isFocused = useIsFocused();
	const navigation = useNavigation();
	const route = useRoute();
	
	const [ loading, setLoading ] = useState( true )
	const [ room, setRoom ] = useState( {} )
	const [ addedAvailableForTeams, setAddedAvailableForTeams ] = useState( [] )
	const [ addedAvailableForEmployees, setAddedAvailableForEmployees ] = useState( [] )
	
	const buildingGuid = _.get( route.params, 'buildingGuid', null );
	const floorGuid = _.get( route.params, 'floorGuid', null );
	const roomGuid = _.get( route.params, 'roomGuid', null );
	
	const renderTabs = () => {
		const tabBarItems = [];
		
		if( roomGuid !== null ) {
			tabBarItems.push({
				key: viewTabs.STATISTICS,
				text: i18n.t( 'management:form_block.title_statistic_other' ),
				scene: ( props ) => {
					return (
						<RoomStatistics
							room={ room }
						/>
					);
				}
			})
		}
		
		tabBarItems.push( {
			key: viewTabs.DATA,
			text: i18n.t( 'management:form_block.title_default' ),
			scene: ( props ) => {
				return (
					<RoomData
		           room={ room }
		           saveForm={ saveForm }
	           />
				);
			}
		});
		
		if( _.get( room, 'guid', null ) !== null ) {
			if( HelperManagementRights.hasViewRight( managementRightsConstants.VIEW.ROOM_TIMESLOTS ) ) {
				tabBarItems.push( {
					key: viewTabs.TIMESLOTS,
					text: HelperString.addArrayLength( i18n.t('management:form_block.title_timeslot' ), ObjectTimeslot.groupTimeSlots( room, { parse: true } ), false ),
					scene: ( props ) => {
						return (
							<ManagementBuildingsComponentsTimeslotsScene
								room={ room }
								onSubmit={ fetchRoom }
								readonly={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.ROOM_TIMESLOTS ) }
							/>
						);
					}
				});
				
				tabBarItems.push( {
					key: viewTabs.TIMESLOT_EXCEPTIONS,
					text: HelperString.addArrayLength( i18n.t( 'management:form_block.title_timeslot_exceptions' ) ),
					scene: ( props ) => {
						return (
							<ManagementBuildingsComponentsTimeslotExceptionsScene
								building={ room }
								onSubmit={ fetchRoom }
								readonly={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.ROOM_TIMESLOTS ) }
							/>
						);
					}
				} );
			}
			
			if( HelperManagementRights.hasViewRight( managementRightsConstants.VIEW.ROOM_TEAMS_EMPLOYEES ) ) {
				if ( room.available_for === HelperAvailability.AVAILABLE_FOR_RESTRICTED ) {
					tabBarItems.push( {
						key: viewTabs.AVAILABLE_FOR_TEAMS,
						text: HelperString.addArrayLength( i18n.t( 'management:form_block.title_available_for_teams' ), addedAvailableForTeams, false ),
						scene: ( props ) => {
							return (
								<ManagementBuildingsComponentsTeamsScene
									room={ room }
									addedAvailableForTeams={ addedAvailableForTeams }
									onSubmit={ fetchRoom }
									readonly={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.ROOM_TEAMS_EMPLOYEES ) }
								/>
							);
						}
					});
				}
				
				if ( room.available_for === HelperAvailability.AVAILABLE_FOR_RESTRICTED_EMPLOYEES ) {
					tabBarItems.push( {
						key: viewTabs.AVAILABLE_FOR_EMPLOYEES,
						text: HelperString.addArrayLength( i18n.t( 'management:form_block.title_available_for_employees' ), addedAvailableForEmployees, false ),
						scene: ( props ) => {
							return (
								<ManagementBuildingsComponentsEmployeeScene
									room={ room }
									addedAvailableForEmployees={ addedAvailableForEmployees }
									onSubmit={ fetchRoom }
									readonly={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.ROOM_TEAMS_EMPLOYEES ) }
								/>
							);
						}
					});
				}
			}
		}
		
		return (
			<TabbedContent
				items={ tabBarItems }
			/>
		)
	}
	
	const saveForm = ( values, { callback } = {} ) => {
		values[ 'floor_guid' ] = floorGuid;
		
		new GraphqlBuilder()
			.addQuery( 'mutation', MutationManagementRoom, values )
			.setOnStart( () => {
				callback( FormStatus.LOADING )
			})
			.setOnSuccess( ( result ) => {
				const room = _.get( result, ['mutation', 'room'], {} );
				
				MutateTranslation.mutate( tableConstants.ROOMS, room.guid, values, {
					callbackSuccess: ( t ) => {
						callback( FormStatus.SUCCESS )
						
						navigation.replace( AppRoutes.MANAGEMENT_BUILDINGS_ROOM, {
							buildingGuid: buildingGuid,
							floorGuid: floorGuid,
							roomGuid: room.guid
						});
					},
					callbackError: () => {
						callback( FormStatus.ERROR )
					}
				})
			})
			.setOnError( ( result ) => {
				callback( FormStatus.ERROR )
			})
			.execute()
	}
	
	const fetchRoom = () => {
		if( roomGuid !== null ) {
			new GraphqlBuilder()
				.addQuery( 'query', QueryManagementRoom, {
					guid: roomGuid
				})
				.setResultPath( ['query', 'room'], {} )
				.setOnSuccess( result => {

					const addedAvailableForEmployees = [];
					_.get( result, ['available_for_employees' ], [] ).forEach( ( employee ) => {
						addedAvailableForEmployees.push( employee.employee );
					});
					
					const addedAvailableForTeams = [];
					_.get( result, ['available_for_teams' ], [] ).forEach( ( team ) => {
						addedAvailableForTeams.push( team.team );
					});
					
					setLoading( false )
					setRoom( result )
					setAddedAvailableForEmployees( addedAvailableForEmployees )
					setAddedAvailableForTeams( addedAvailableForTeams )
				})
				.setOnError( setLoading.bind( this, false ) )
				.execute()

			return;
		}

		setLoading( false )
		setRoom( ObjectRoom.defaults )
	}

	useEffect( () => {
		return fetchRoom();
	}, [ isFocused, roomGuid ] )
	
	let content = <LoadingComponent />
	if( !loading ) {
		content = renderTabs()
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				topNavigation={true}
				topNavigationProps={{
					showBackButton: true
				}}
				title={ i18n.t('management:pages.buildings.room.title', {
					name: Translate.field( room, 'name' ),
					interpolation: {
						escapeValue: false
					}
				})}>
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

export default ManagementBuildingRoomScene
